import {inject, Injectable} from './di';
import {Logger} from './logger';
import {LegacyLogService} from './logger/legacy-log-service';
import {LegacyPersistentLogCategory, LegacyPersistentLogService} from './logger/legacy-persistent-log-service';
import {RemoteDebugService} from './remote-debug-service';
import {StorageService} from './storage-service';
import {LoaderService} from './loader/loader-service';
import {TvModuleService} from './modules/tv-module-service';
import {ConfigService} from './config/config-service';
import {TvModuleName} from './modules/tv-module-name';

@Injectable()
export class ServiceCodesService {
  private readonly logger = inject(Logger);
  private readonly legacyLogService = inject(LegacyLogService);
  private readonly configService = inject(ConfigService);

  private listeners: Record<ServiceCode, ServiceCodesListener> = {};

  private oldCodes: Record<ServiceCode, ServiceCodesListener> = {
    '1105': () => {
      window.tv_configure();
    },
    '1106': () => {
      storage.removeItem('token');
      window.tv_log('Guest token cleared');
    },
    '1120': () => {
      $('#tv_fullscreen_weather').html('???');
      window.tv_weather();
    },
    '1130': () => {
      try {
        Apps.miracast();
      } catch (err) {
        this.logger.error(`[ServiceCodesService] Miracast/WiDi failed`, err);
        this.legacyLogService.add('Miracast/WiDi failed');
      }
    },
    '1133': () => {
      try {
        _tv_sources();
      } catch (err) {
        this.logger.error(`[ServiceCodesService] Sources List failed`, err);
        this.legacyLogService.add('Sources List failed');
      }
    },
    '1134': () => {
      try {
        _tv_sources(true);
      } catch (err) {
        this.logger.error(`[ServiceCodesService] Sources List failed`, err);
        this.legacyLogService.add('Sources List failed');
      }
    },
    '1135': () => {
      window.tv_welcome();
    },
    '1136': () => {
      window.tv_welcome_hide();
    },
    '1137': () => {
      try {
        window.navigate('#sources_page');
      } catch (err) {
        this.logger.error(`[ServiceCodesService] Navigation to the Sources Page was failed`, err);
        this.legacyLogService.add('Sources Page List failed');
      }
    },
    '1138': () => {
      try {
        window.navigate('#sources_page_all');
      } catch (err) {
        this.logger.error(`[ServiceCodesService] Navigation to the Sources Page List was failed`, err);
        this.legacyLogService.add('Sources Page List failed');
      }
    },
    '1150': () => {
      //Classic menu
    },
    '1151': () => {
      build_metro_menu();
    },
    '1165': () => {
      window.CONFIG.test();
    },
    '1168': () => {
      this.legacyLogService.modules();
    },
    '1169': () => {
      this.legacyLogService.out();
    },
    '1170': () => {
      void this.legacyLogService.send();
    },
    '1171': () => {
      const remoteDebugService = inject(RemoteDebugService);
      void remoteDebugService.startRemoteDebugSession(true);
    },
    '1172': () => {
      const remoteDebugService = inject(RemoteDebugService);
      remoteDebugService.stopRemoteDebugSession();
    },
    '1173': () => {
      window.hoteza.loadGuard.toggleOverlay();
    },
    '1175': () => {
      const storageService = inject(StorageService);
      const hhurl = storageService.getString('hhurl');
      if (hhurl) {
        document.location = hhurl + '#del';
      } else {
        this.logger.warn(`[ServiceCodesService] Hoteza was launched not from Hoteza HUB`);
        window.custom_alert('HOTEZA HUB: not on hub');
        this.legacyLogService.add('HOTEZA HUB: not on hub');
      }
    },
    '1191': () => {
      window._log_show = true;
      window.tv_log('+');
    },
    '1199': () => {
      window._log_show = false;
      window.tv_log('-');
    },
    '1666': () => {
      window.fire_alarm();
    },
    '1777': () => {
      window.tv_log(window.perfs());
    },
    '1800': () => {
      const tvModuleService = inject(TvModuleService);

      if (!tvModuleService.checkModule(TvModuleName.Tests)) {
        const loaderService = inject(LoaderService);
        loaderService.start();
        tvModuleService
          .load(TvModuleName.Tests)
          .then(() => loaderService.stop())
          .catch(() => loaderService.stop());
      } else {
        // TODO inject test module
        window.Tests.open();
      }
    },
    '1801': () => {
      UI.build_page({
        id: 'pincode',
        title: ' ',
        content: `<div style="font-size:72px;line-height:initial;text-align:center;margin-top:100px;">PINCODE<br/>${window.pincode()}</div>`,
      });
      window.navigate('#pincode');
    },
    '100': () => {
      window._tv_bg_restore();

      this.logger.warn(`[ServiceCodesService] Rebooting the TV due to the entered service code 100.`);

      const legacyPersistentLogService = inject(LegacyPersistentLogService);
      legacyPersistentLogService.add('service code', LegacyPersistentLogCategory.Reload);
      this.legacyLogService.add('TV: reloading, code 100');

      window.reload_app();
    },
    '101': () => {
      $(document.head)
        .find('link')
        .each(function () {
          $(this).attr('href', $(this).attr('href').replace(/\?.+$/, '') + '?_=' + Math.random());
        });
    },
    '110': () => {
      if (window.$id('customization_css')) {
        $('#customization_css').remove();
        $('#customization_css_old').remove();
      } else {
        window.css_append('custom/config.css?_=' + Math.random(), 'customization_css');
      }
    },
  };

  constructor() {
    window.ServiceCodes = this;
  }

  /**
   * Register service code listeners
   * @param code Service code
   * @param callback function that will be called if the given service code is activated.
   */
  registerListener(code: ServiceCode, callback: ServiceCodesListener): boolean;

  /**
   * Register service code listeners
   * @param codes List of service codes
   * @param callback function that will be called if the given service code is activated.
   */
  registerListener(codes: Array<ServiceCode>, callback: ServiceCodesListener): boolean;

  /**
   * Register service code listeners
   * @param mapping An object whose keys are service codes and whose values are callbacks, which are functions that
   * will be called if the given service code is activated.
   */
  registerListener(mapping: Record<ServiceCode, ServiceCodesListener>): boolean;

  /**
   * Register Service Code listener
   * @param code Service Code, array of Service Codes, or object where keys are service codes and values are callbacks.
   * @param func callback, a function that will be called if the given Service Code is activated.
   */
  public registerListener(
    code: ServiceCode | Array<ServiceCode> | Record<ServiceCode, ServiceCodesListener>,
    func?: ServiceCodesListener,
  ): boolean {
    if (typeof code === 'object') {
      if (Array.isArray(code)) {
        if (!func || typeof func !== 'function') {
          this.logger.error(`[ServiceCodesService.registerListener] You should pass callback as the second parameter`);
          return false;
        }
        for (const o of code) {
          this.registerListener(o, func);
        }
      } else {
        for (const i in code) {
          this.registerListener(i, code[i]);
        }
      }
      return true;
    }

    if (code in this.listeners) {
      this.logger.warn(`[ServiceCodesService.registerListener] This service code is already being monitored.`, code);
      this.legacyLogService.add('ServiceCodes: error code exists ' + code);
      return false;
    }

    if (!code) {
      this.logger.error(`[ServiceCodesService.registerListener] Service Codes should be specified`);
      return false;
    }
    if (code.length !== 4 && /^\d+$/.test(code)) {
      this.logger.error(`[ServiceCodesService.registerListener] Numeric Service Codes should contain exactly 4 digits`);
      this.legacyLogService.add('ServiceCodes: error length of code ' + code);
      return false;
    }
    if (!func || typeof func !== 'function') {
      this.logger.error(`[ServiceCodesService.registerListener] You should pass callback as the second parameter`);
      return false;
    }

    this.listeners[code] = func;

    return true;
  }

  /**
   * Find the callback for the specified Service Code and execute it.
   * @param code Service Code
   * @return true if callback was found and successfully executed. Otherwise - false
   */
  public evaluate(code: ServiceCode) {
    const config = this.configService.config;

    if (config.tv?.service_codes_lock) {
      this.logger.warn(
        `[ServiceCodesService.evaluate] Can not execute Service Code because it was disabled by config.tv.service_codes_lock`,
      );
      this.legacyLogService.add("ServiceCodes: can't execute, LOCKED");
      return false;
    }

    if (code in this.listeners) {
      try {
        this.listeners[code]();
        return true;
      } catch (err) {
        this.logger.error(
          `[ServiceCodesService.evaluate] An unexpected error occurred while executing the callback for service code ${code}.`,
          err,
        );
        this.legacyLogService.add(`ServiceCodes: error executing callback code ${code}`);
      }
    }

    if (code in this.oldCodes) {
      try {
        this.oldCodes[code]();
        return true;
      } catch (err) {
        this.logger.error(
          `[ServiceCodesService.evaluate] An unexpected error occurred while executing the callback for service code ${code}.`,
          err,
        );
        this.legacyLogService.add('ServiceCodes: error executing old mechanism code ' + code);
      }
    }

    return false;
  }

  /**
   * Check that a handler exists for the selected Service Code.
   * @param code Service Code
   */
  public checkCodes(code: ServiceCode) {
    return code in this.listeners || code in this.oldCodes;
  }
}

type ServiceCodesListener = () => unknown;
type ServiceCode = string;

declare global {
  interface Window {
    ServiceCodes: ServiceCodesService;
  }
  const ServiceCodes: ServiceCodesService;
}
